import {
  Children,
  ReactElement,
  cloneElement,
  isValidElement,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { Button } from '@/common/components/Button/Button';
import { Modal, TModalProps } from '@/common/components/Modal/Modal';
import { TOverlayRef } from '@/common/components/Overlay/Overlay.types';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { styles } from './ModalLearning.styles';
import {
  ModalLearningStep,
  TModalLearningStepProps,
} from './ModalLearningStep/ModalLearningStep';

export type TModalLearningProps = Pick<
  TModalProps,
  'modalRef' | 'heightClass'
> & {
  className?: string;
  assetContainerSize?: 'md' | 'lg';
  children:
    | ReactElement<TModalLearningStepProps>
    | (ReactElement<TModalLearningStepProps> | null | undefined)[];
  onClose: () => void;
};

function ModalLearningRoot({
  className,
  children,
  onClose,
  modalRef,
  assetContainerSize = 'md',
  ...modalProps
}: TModalLearningProps) {
  const breakpoints = useBreakpoints(['sm']);
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const localRef = useRef<TOverlayRef>(null);
  const ref = modalRef ?? localRef;

  const validChildren = Children.toArray(children).filter(
    isValidElement<TModalLearningStepProps>
  );
  const isLastStep = currentStep === validChildren.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      ref.current?.close();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep((step) => (step > 0 ? step - 1 : 0));
  };

  const currentChild = validChildren[currentStep];

  const isOnlyChild = validChildren.length === 1;

  return (
    <Modal
      {...modalProps}
      heightClass={!isOnlyChild ? modalProps.heightClass : undefined}
      modalRef={ref}
      onClose={onClose}
      backgroundColor="accentSecondary10"
      size="md"
      backgroundComponent={
        <View className={styles.circle({ size: assetContainerSize })} />
      }
      scrollable={false}
      contentPadding="none"
      headerProps={{ className: styles.header }}
      footer={
        isOnlyChild ? (
          breakpoints.sm ? (
            <View className="pb-lg" />
          ) : null
        ) : (
          <Stack
            horizontal
            align="center"
            justify={breakpoints.sm ? 'end' : 'between'}
            gap="md"
          >
            <Stack horizontal gap="sm" grow className={styles.steps}>
              {validChildren.map((_, idx) => (
                <StepDot key={idx} active={currentStep === idx} />
              ))}
            </Stack>
            <View>
              {currentStep > 0 && (
                <Button variant="tertiary" onPress={handleBack}>
                  {t('general.navigation.back')}
                </Button>
              )}
            </View>
            <Button
              variant={breakpoints.sm ? 'primary' : 'tertiary'}
              onPress={handleNext}
            >
              {isLastStep
                ? t('general.navigation.close')
                : t('general.navigation.next')}
            </Button>
          </Stack>
        )
      }
      footerProps={{ border: false, className: styles.footer }}
    >
      {cloneElement(currentChild, {
        ...currentChild.props,
        assetContainerSize,
      })}
    </Modal>
  );
}

function StepDot({ active }: { active: boolean }) {
  const style = useAnimatedStyle(() => ({
    width: withTiming(active ? 20 : 4),
  }));
  return <Animated.View className={styles.step({ active })} style={style} />;
}

export const ModalLearning = {
  Root: ModalLearningRoot,
  Step: ModalLearningStep,
};
